import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Paperclip, PlayCircle, File, Edit3, Eye } from 'react-feather';
import { integrationsAPI } from '../services/integrationsAPI';
import { athleteAPI, userAPI, athleteSharingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { mediaUploadService } from '../services/mediaUploadService';
import Modal, { ModalHandle } from './Modal';
import YouTubeVideoPickerModal from './YouTubeVideoPickerModal';
import TemplatePickerModal from './TemplatePickerModal';
import { useEmailVariables } from '../hooks/useEmailVariables';
import { QuickTags } from './QuickTags';
import { QuickTag } from '../utils/emailVariables';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput, TextArea } from './ui';

interface ThreadData {
  thread_id: string;
  subject: string;
  messages: Array<{
    message_id: string;
    from_email: string;
    to_emails: string[];
    subject: string;
    body: string;
    timestamp: string;
    is_read: boolean;
  }>;
}

interface ThreadReplyModalProps {
  visible: boolean;
  onClose: () => void;
  threadId: string;
  threadData?: ThreadData;
  athleteId: string;
  onSuccess?: () => Promise<void>;
}

interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'document';
}

interface AthleteProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  graduationYear?: number;
  city?: string;
  state?: string;
  sport?: string;
  grade?: number;
  position?: string;
  createdBy?: string;
  createdAt?: string;
  youtube_channel_url?: string;
  youtube_channel_handle?: string;
}

interface AcademicData {
  satMath?: string;
  satReadingWriting?: string;
  satTotal?: string;
  psatMath?: string;
  psatReadingWriting?: string;
  psatTotal?: string;
  actTotal?: string;
  gpaScale?: string;
  currentGpa?: string;
}

interface PerformanceMetrics {
  sixty_yard_time?: number | null;
  forty_yard_time?: number | null;
  twenty_yard_time?: number | null;
  ten_yard_time?: number | null;
  home_to_first?: number | null;
  pop_time?: number | null;
  exit_velocity?: number | null;
  overhand_velocity?: number | null;
  grip_strength?: number | null;
}

interface SportInfo {
  sport: string;
  positions: string[];
  bats: string;
  throws: string;
  height: string;
  weight: number;
}

// Strip any merge tags the resolver couldn't fill, plus the now-empty "Label:"
// lines they leave behind (e.g. an empty "GPA:") so coaches never receive
// literal {{...}} or dangling labels (#199). Mirrors EmailCoachModal's strip.
const stripUnresolvedTags = (text: string): string =>
  text
    .replace(/\{\{\s*[^}]*?\s*\}\}/g, '')
    .replace(/^[ \t]*[A-Za-z][A-Za-z .]*:[ \t]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const ThreadReplyModal: React.FC<ThreadReplyModalProps> = ({
  visible,
  onClose,
  threadId,
  threadData,
  athleteId,
  onSuccess,
}) => {
  const { sessionToken } = useAuth();
  const { showToast } = useToast();
  const { isMobile } = useResponsive();
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfile | null>(null);
  const [sportInfo, setSportInfo] = useState<SportInfo | null>(null);
  const [academicData, setAcademicData] = useState<AcademicData | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<{
    school?: { name: string };
    travelClub?: { name: string };
  } | null>(null);
  // Public profile share link — only set when the athlete has sharing on;
  // drives the {{athlete.profileShareLink}} tag + the hint below the editor.
  const [profileShareUrl, setProfileShareUrl] = useState<string | undefined>(undefined);
  const [replyBody, setReplyBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<MediaFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [youtubePickerVisible, setYoutubePickerVisible] = useState(false);
  const [templatePickerVisible, setTemplatePickerVisible] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use shared email variables system
  const { tags, resolveVariables } = useEmailVariables({
    athlete: athleteProfile,
    sportInfo,
    academicData,
    performanceMetrics,
    schoolInfo,
    profileShareUrl,
    context: {
      hasCoach: false,
      hasYouTube: !!athleteProfile?.youtube_channel_url,
      isThreadReply: false // Use placeholders like other modals
    }
  });

  // Load athlete data when modal opens
  useEffect(() => {
    if (visible && sessionToken && athleteId) {
      // Clear stale data first, then load fresh data
      setAthleteProfile(null);
      setSchoolInfo(null);
      setSportInfo(null);
      setAcademicData(null);
      setPerformanceMetrics(null);
      setProfileShareUrl(undefined);
      loadAthleteData();
      loadProfileShareUrl();
    } else if (!visible) {
      // Clear cached athlete data when modal closes to prevent stale data
      setAthleteProfile(null);
      setSchoolInfo(null);
      setSportInfo(null);
      setAcademicData(null);
      setPerformanceMetrics(null);
      setProfileShareUrl(undefined);
    }
  }, [visible, sessionToken, athleteId]);

  // Fetch the athlete's public profile share URL. Resolves to a value only
  // when sharing is enabled; otherwise stays undefined (tag not offered, hint
  // shown). athleteId is a prop here, so no family lookup is needed.
  const loadProfileShareUrl = async () => {
    if (!sessionToken || !athleteId) return;
    try {
      const res = await athleteSharingAPI.getShareSettings(sessionToken, athleteId);
      if (res?.data?.shareUrl) setProfileShareUrl(res.data.shareUrl);
    } catch {
      /* sharing not enabled or fetch failed — tag stays unavailable */
    }
  };

  // Check Gmail connection
  useEffect(() => {
    const checkConnection = async () => {
      if (!athleteId || !visible) return;

      try {
        setIsCheckingConnection(true);
        const connectionStatus = await integrationsAPI.getConnectionStatus(athleteId);
        setIsGmailConnected(connectionStatus.gmail);
      } catch (error) {
        setIsGmailConnected(false);
      } finally {
        setIsCheckingConnection(false);
      }
    };

    checkConnection();
  }, [athleteId, visible]);

  const loadAthleteData = async () => {
    if (!sessionToken || !athleteId) return;

    try {
      // Use the athleteId prop directly (already passed from parent)
      const profileData = await userAPI.getComprehensiveProfile(sessionToken);
      if (profileData.athletes && profileData.athletes.length > 0) {
        const athlete = profileData.athletes.find(
          (a: any) => a.id === athleteId
        );
        if (athlete) {
          setAthleteProfile({
            id: athlete.id,
            firstName: athlete.firstName,
            lastName: athlete.lastName,
            email: athlete.email,
            phone: athlete.phone,
            dateOfBirth: athlete.dateOfBirth,
            graduationYear: athlete.graduationYear,
            city: athlete.city,
            state: athlete.state,
            sport: athlete.sport,
            grade: parseInt(athlete.grade) || undefined,
            position: athlete.position,
            createdBy: athlete.createdBy,
            createdAt: athlete.createdAt,
            youtube_channel_url: (athlete as any).youtube_channel_url,
            youtube_channel_handle: (athlete as any).youtube_channel_handle,
          });

          // Load sport info
          if ((athlete as any).sportInfo) {
            const sportData = (athlete as any).sportInfo;
            setSportInfo({
              sport: sportData.sport,
              positions: typeof sportData.positions === 'string' 
                ? sportData.positions.split(', ').filter((p: string) => p.trim())
                : Array.isArray(sportData.positions) 
                  ? sportData.positions
                  : [],
              bats: sportData.bats,
              throws: sportData.throws,
              height: sportData.height,
              weight: sportData.weight,
            });
          }

          // Load school info
          if ((athlete as any).schoolInfo) {
            setSchoolInfo((athlete as any).schoolInfo);
          }

          // Load academic data using the athleteId prop
          try {
            const academic = await athleteAPI.getAcademicPerformance(
              sessionToken,
              athleteId
            );
            if ((academic as any).success && (academic as any).data?.academicPerformance) {
              setAcademicData((academic as any).data.academicPerformance);
            }
          } catch (error) {
            console.log('No academic data available');
          }

          // Load performance metrics using the athleteId prop
          try {
            const metrics = await athleteAPI.getPerformanceMetrics(
              sessionToken,
              athleteId
            );
            if ((metrics as any).success && (metrics as any).data?.metrics) {
              setPerformanceMetrics((metrics as any).data.metrics);
            }
          } catch (error) {
            console.log('No performance metrics available');
          }
        }
      }
    } catch (error) {
      console.error('Error loading athlete data:', error);
    }
  };

  const insertQuickTag = (tag: QuickTag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = replyBody;
    const newText = currentText.substring(0, start) + tag.value + currentText.substring(end);

    setReplyBody(newText);

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + tag.value.length;
      textarea.focus();
    }, 0);
  };

  const handleYouTubeVideosSelected = (videoLinks: string[]) => {
    const videosText = videoLinks.join('\n');
    const newBody = replyBody ? `${replyBody}\n\n${videosText}` : videosText;
    setReplyBody(newBody);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: MediaFile[] = [];

    for (let i = 0; i < Math.min(files.length, 4 - attachedMedia.length); i++) {
      const file = files[i];

      if (file.size > 10 * 1024 * 1024) {
        showToast(`File ${file.name} is too large. Maximum size is 10MB.`, 'warning');
        continue;
      }

      let type: 'image' | 'video' | 'document' = 'document';
      if (file.type.startsWith('image/')) type = 'image';
      else if (file.type.startsWith('video/')) type = 'video';

      const preview = URL.createObjectURL(file);
      newFiles.push({ file, preview, type });
    }

    if (newFiles.length > 0) {
      setAttachedMedia([...attachedMedia, ...newFiles]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMediaFile = (index: number) => {
    try {
      URL.revokeObjectURL(attachedMedia[index].preview);
    } catch (e) {}
    setAttachedMedia(attachedMedia.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const updated = { ...prev };
      delete updated[`media_${index}`];
      return updated;
    });
  };

  const getRecipients = (): string[] => {
    if (!threadData) return [];

    const sortedMessages = [...threadData.messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // TODO(audit #209): the athlete's *connected Gmail* address isn't available
    // in this component; prefer it over these proxies once it lands.
    //
    // Which addresses count as "self" (and must be excluded from the reply):
    //  - the athlete's profile email — safer than "whoever sent the first
    //    message", which mislabelled the reply when a coach started the thread;
    //  - the sole sender of a single-message thread, which is always the
    //    athlete's own sent email (see GmailDetailModal's synthesized reply
    //    data) — this keeps a follow-up on a single sent email from CC'ing the
    //    athlete's own inbox.
    const selfEmails = new Set<string>();
    if (athleteProfile?.email) selfEmails.add(athleteProfile.email.toLowerCase());
    if (sortedMessages.length === 1 && sortedMessages[0]?.from_email) {
      selfEmails.add(sortedMessages[0].from_email.toLowerCase());
    }
    if (selfEmails.size === 0 && sortedMessages[0]?.from_email) {
      selfEmails.add(sortedMessages[0].from_email.toLowerCase());
    }

    if (selfEmails.size === 0) return [];

    const allEmailsInThread = new Set<string>();

    threadData.messages.forEach((msg) => {
      if (msg.from_email && !selfEmails.has(msg.from_email.toLowerCase())) {
        allEmailsInThread.add(msg.from_email);
      }
      msg.to_emails.forEach((email) => {
        if (email && !selfEmails.has(email.toLowerCase())) {
          allEmailsInThread.add(email);
        }
      });
    });

    return Array.from(allEmailsInThread);
  };

  const handleSendReply = async () => {
    if (!threadData || !replyBody.trim()) {
      showToast('Please enter a message', 'warning');
      return;
    }
    if (getRecipients().length === 0) {
      showToast("Couldn't determine who to reply to", 'error');
      return;
    }

    // Validate Cc/Bcc addresses up front so a malformed token can't be sent.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badRecipient = [...ccEmails.split(','), ...bccEmails.split(',')]
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .find(e => !EMAIL_RE.test(e));
    if (badRecipient) {
      showToast(`"${badRecipient}" is not a valid email address. Check your Cc/Bcc.`, 'error');
      return;
    }

    setIsSending(true);

    try {
      const recipients = getRecipients();
      const subject = threadData.subject.startsWith('Re:')
        ? threadData.subject
        : `Re: ${threadData.subject}`;

      let attachmentObjects: { filename: string; url: string; content_type: string; size?: number }[] = [];

      if (attachedMedia.length > 0) {
        setIsUploading(true);
        const uploaded = await mediaUploadService.uploadMultipleMedia(
          attachedMedia,
          athleteId,
          sessionToken || '',
          (index, progress) => {
            setUploadProgress(prev => ({ ...prev, [`media_${index}`]: progress.percentage }));
          }
        );

        const failed = uploaded.filter(u => !u.success);
        if (failed.length > 0) {
          throw new Error('Some attachments failed to upload');
        }

        attachmentObjects = uploaded.map((u, i) => ({
          filename: attachedMedia[i].file.name,
          url: u.fileUrl || '',
          content_type: attachedMedia[i].file.type,
          size: attachedMedia[i].file.size,
        }));

        setIsUploading(false);
      }

      // Resolve placeholders to actual values before sending, then strip any
      // tags the resolver couldn't fill (and dangling "GPA:" lines) so coaches
      // never receive literal {{...}} or empty labels (#199).
      const resolvedBody = stripUnresolvedTags(resolveVariables(replyBody.trim()));

      // Parse CC/BCC emails
      const parsedCc = ccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);
      const parsedBcc = bccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);

      await integrationsAPI.sendThreadReply(athleteId, threadId, {
        recipients,
        subject,
        body: resolvedBody,
        cc_emails: parsedCc.length > 0 ? parsedCc : undefined,
        bcc_emails: parsedBcc.length > 0 ? parsedBcc : undefined,
        attachments: attachmentObjects.length > 0 ? attachmentObjects : undefined,
      });

      showToast('Reply sent.', 'success');
      setReplyBody('');
      setCcEmails('');
      setBccEmails('');
      setShowCcBcc(false);
      setAttachedMedia([]);

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (error: any) {
      showToast(error?.message || 'Failed to send reply', 'error');
    } finally {
      setIsSending(false);
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    if (!isSending) {
      setReplyBody('');
      setCcEmails('');
      setBccEmails('');
      setShowCcBcc(false);
      setAttachedMedia([]);
      onClose();
    }
  };

  const getRecipientsPreview = (): string => {
    const recipients = getRecipients();
    if (recipients.length === 0) return 'Unknown';
    if (recipients.length === 1) return recipients[0];
    if (recipients.length === 2) return recipients.join(', ');
    return `${recipients[0]}, ${recipients[1]} +${recipients.length - 2} more`;
  };

  if (!visible) return null;

  if (isCheckingConnection) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px',
        }}
        onClick={handleClose}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '48px 32px',
            maxWidth: '400px',
            textAlign: 'center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E5E7EB',
              borderTop: '4px solid #F9550D',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ margin: 0, color: '#6B7280' }}>Checking Gmail Connection...</p>
        </div>
      </div>
    );
  }

  if (!isGmailConnected) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px',
        }}
        onClick={handleClose}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '48px 32px',
            maxWidth: '400px',
            textAlign: 'center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              background: '#FEE2E2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={32} color="#EF4444" />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
            Gmail Account Not Connected
          </h3>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
            To send thread replies, you need to connect your Gmail account first.
          </p>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              background: '#F9550D',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={visible}
        onClose={handleClose}
        ref={composerModalRef}
        isDirty={replyBody.trim().length > 0}
        disableClose={isSending || youtubePickerVisible || templatePickerVisible}
        ariaLabel="Reply to Thread"
        zIndex={1001}
        contentStyle={{
          background: 'white',
          borderRadius: '12px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
          {/* Header */}
          <div
            style={{
              backgroundColor: '#151133',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isSending}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: isSending ? 'not-allowed' : 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <X size={24} />
            </button>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: 'white',
              }}
            >
              Reply to Thread
            </h2>
            <div style={{ width: '40px' }} />
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              backgroundColor: '#F9FAFB',
              padding: '24px',
            }}
          >
            {/* Thread Info Card */}
            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  background: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EA4335"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px',
                  }}
                >
                  {threadData?.subject || 'Email Thread'}
                </div>
                <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                  To: {getRecipientsPreview()}
                </div>
              </div>
            </div>

            {/* CC/BCC Section */}
            <div style={{
              background: 'white',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}>
              {!showCcBcc ? (
                <button
                  onClick={() => setShowCcBcc(true)}
                  disabled={isSending}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#6B7280',
                    cursor: 'pointer',
                    padding: '4px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#F9550D')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
                >
                  + Add CC / BCC
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label htmlFor="thread-reply-cc" style={{
                      display: 'block', fontSize: '12px', fontWeight: '600',
                      color: '#6B7280', marginBottom: '4px',
                      textTransform: 'uppercase' as const, letterSpacing: '0.5px'
                    }}>
                      CC
                    </label>
                    <TextInput
                      id="thread-reply-cc"
                      type="text"
                      value={ccEmails}
                      onChange={(e) => setCcEmails(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      disabled={isSending}
                      style={{
                        padding: '10px 14px',
                        border: '1px solid #D1D5DB', borderRadius: '8px',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="thread-reply-bcc" style={{
                      display: 'block', fontSize: '12px', fontWeight: '600',
                      color: '#6B7280', marginBottom: '4px',
                      textTransform: 'uppercase' as const, letterSpacing: '0.5px'
                    }}>
                      BCC
                    </label>
                    <TextInput
                      id="thread-reply-bcc"
                      type="text"
                      value={bccEmails}
                      onChange={(e) => setBccEmails(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      disabled={isSending}
                      style={{
                        padding: '10px 14px',
                        border: '1px solid #D1D5DB', borderRadius: '8px',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                  <button
                    onClick={() => { setShowCcBcc(false); setCcEmails(''); setBccEmails(''); }}
                    style={{
                      alignSelf: 'flex-start', background: 'none', border: 'none',
                      fontSize: '12px', color: '#9CA3AF', cursor: 'pointer', padding: '2px 0'
                    }}
                  >
                    Remove CC/BCC
                  </button>
                </div>
              )}
            </div>

            {/* Reply Form Card */}
            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              {/* Compose / Preview Toggle */}
              <div style={{
                display: 'flex',
                backgroundColor: '#F3F4F6',
                borderRadius: '8px',
                padding: '3px',
                marginBottom: '16px',
                gap: '4px'
              }}>
                <button
                  onClick={() => setPreviewMode(false)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: !previewMode ? '#151133' : 'transparent',
                    color: !previewMode ? 'white' : '#6B7280',
                    transition: 'all 0.2s'
                  }}
                >
                  <Edit3 size={14} style={{ marginRight: 6 }} /> Compose
                </button>
                <button
                  onClick={() => setPreviewMode(true)}
                  disabled={!replyBody.trim()}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: !replyBody.trim() ? 'not-allowed' : 'pointer',
                    backgroundColor: previewMode ? '#151133' : 'transparent',
                    color: previewMode ? 'white' : '#6B7280',
                    opacity: !replyBody.trim() ? 0.4 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  <Eye size={14} style={{ marginRight: 6 }} /> Preview
                </button>
              </div>

              {previewMode ? (
                /* Preview Mode */
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Reply</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#1F2937',
                      padding: '16px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.6',
                      minHeight: '150px'
                    }}>
                      {stripUnresolvedTags(resolveVariables(replyBody)) || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No message</span>}
                    </div>
                  </div>
                  {attachedMedia.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px', fontWeight: '500' }}>Attachments ({attachedMedia.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {attachedMedia.map((media, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#374151'
                          }}>
                            {media.file.name || `Attachment ${idx + 1}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Compose Mode */
                <>
              {/* Message Input */}
              <div style={{ marginBottom: '16px' }}>
                <TextArea
                  ref={textareaRef}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type your reply here..."
                  aria-label="Your reply"
                  disabled={isSending}
                  style={{
                    minHeight: '150px',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </div>

              {/* Use Template Button */}
              <button
                onClick={() => setTemplatePickerVisible(true)}
                disabled={isSending}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#FFF7ED',
                  border: '2px solid #f9550d',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#f9550d',
                  opacity: isSending ? 0.5 : 1,
                  marginBottom: '12px',
                }}
                onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFEDD5')}
                onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFF7ED')}
              >
                <File size={18} />
                Use Template
              </button>

              {/* Quick Tags */}
              <QuickTags
                tags={tags}
                onInsertTag={insertQuickTag}
                disabled={isSending}
              />

              {/* Hint: Profile Share Link tag only offered when sharing is on. */}
              {!profileShareUrl && (
                <p style={{
                  margin: '8px 0 0',
                  fontSize: '12px',
                  color: '#6B7280',
                  lineHeight: 1.4,
                }}>
                  Tip: Turn on profile sharing in your profile's Share settings to add a
                  <strong> Profile Share Link</strong> tag to your replies.
                </p>
              )}

              {/* YouTube Videos Button */}
              <button
                onClick={() => setYoutubePickerVisible(true)}
                disabled={isSending}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'white',
                  border: '2px solid #FF0000',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#FF0000',
                  opacity: isSending ? 0.5 : 1,
                  marginBottom: '16px',
                }}
              >
                <PlayCircle size={20} />
                YouTube Videos
              </button>

              {/* Attachments */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending || isUploading || attachedMedia.length >= 4}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: 'white',
                    border: '2px dashed #F9550D',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: (isSending || isUploading || attachedMedia.length >= 4) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#F9550D',
                    marginBottom: '12px',
                    opacity: (isSending || isUploading || attachedMedia.length >= 4) ? 0.5 : 1,
                  }}
                >
                  <Paperclip size={20} />
                  {isUploading ? 'Uploading...' : 'Add File'}
                </button>

                {attachedMedia.length > 0 && (
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', overflowX: 'auto' }}>
                    {attachedMedia.map((media, index) => (
                      <div key={index} style={{ position: 'relative', flexShrink: 0 }}>
                        {media.type === 'document' ? (
                          <div
                            style={{
                              width: '100px',
                              height: '100px',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px',
                            }}
                          >
                            <div style={{ fontSize: '10px', color: '#374151', textAlign: 'center', wordBreak: 'break-word' }}>
                              {media.file.name}
                            </div>
                          </div>
                        ) : (
                          <img
                            src={media.preview}
                            alt="preview"
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        )}
                        <button
                          onClick={() => removeMediaFile(index)}
                          disabled={isSending || isUploading}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(239,68,68,0.9)',
                            border: '1px solid white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          <X size={14} color="white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                  Maximum file size: 10MB.<br />
                  Supported formats: PDF, DOC, images, and more.
                </div>
              </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '20px 24px',
              borderTop: '1px solid #E5E7EB',
              backgroundColor: 'white',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              display: 'flex',
              gap: '12px',
            }}
          >
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isSending}
              style={{
                flex: 1,
                padding: '14px',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                cursor: isSending ? 'not-allowed' : 'pointer',
                opacity: isSending ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSendReply}
              disabled={isSending || !replyBody.trim()}
              style={{
                flex: 1,
                padding: '14px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: (isSending || !replyBody.trim()) ? '#9CA3AF' : '#F9550D',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: (isSending || !replyBody.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Send size={16} />
              {isSending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </Modal>

      {/* YouTube Video Picker Modal */}
      <YouTubeVideoPickerModal
        isOpen={youtubePickerVisible}
        onClose={() => setYoutubePickerVisible(false)}
        onSelectVideos={handleYouTubeVideosSelected}
        title="Select Videos"
      />

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={templatePickerVisible}
        onClose={() => setTemplatePickerVisible(false)}
        onApply={(template) => {
          setReplyBody(template.body);
        }}
      />

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};
